/**
 * Locale-aware date, time, number, and currency formatters.
 *
 * CRITICAL design rules:
 *  1. Every formatter that shows a hotel operational timestamp accepts an
 *     explicit IANA `timeZone` argument (e.g. 'Europe/Sofia').
 *     Do NOT default to the browser timezone for hotel-facing displays.
 *  2. Pass `timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone`
 *     (or any user-preference value) when you want to show the viewer's
 *     local time instead of the hotel's operational timezone.
 *  3. `formatRelativeTime` uses date-fns for human-readable relative strings
 *     because the Intl.RelativeTimeFormat API requires the caller to compute
 *     the unit and value, which is error-prone across DST boundaries.
 */

import {
  type Locale as DateFnsLocale,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  formatDistance,
  formatDistanceToNow,
} from 'date-fns';
import { bg, de, enUS, fr } from 'date-fns/locale';
import { useMemo } from 'react';
import type { SupportedLocale } from './config.js';
import { useLocale } from './react.js';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const DATE_FNS_LOCALE_MAP: Record<SupportedLocale, DateFnsLocale> = {
  en: enUS,
  bg: bg,
  de: de,
  fr: fr,
};

function dateFnsLocale(locale: SupportedLocale): DateFnsLocale {
  return DATE_FNS_LOCALE_MAP[locale]!;
}

/** Coerce a Date, ISO string, or timestamp number to a Date. */
function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface TimeZoneOptions {
  /**
   * IANA timezone identifier for the display.
   * Use the hotel's `timezone` field from the resolved hotel context for
   * operational timestamps.
   * Use `Intl.DateTimeFormat().resolvedOptions().timeZone` for "your local
   * time" display.
   */
  timeZone: string;
}

// ---------------------------------------------------------------------------
// Date and time formatters
// ---------------------------------------------------------------------------

/**
 * Formats a date as a locale-appropriate date string (no time component).
 *
 * @example
 *   formatDate('2024-06-07T14:30:00Z', 'en', { timeZone: 'Europe/Sofia' })
 *   // → "Jun 7, 2024"
 */
export function formatDate(
  value: Date | string | number,
  locale: SupportedLocale,
  options: TimeZoneOptions,
): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: options.timeZone,
  }).format(toDate(value));
}

/**
 * Formats a time as a locale-appropriate time string (hours + minutes).
 * Uses 24-hour clock for bg/de; 12-hour for en/fr follows locale default.
 *
 * @example
 *   formatTime('2024-06-07T14:30:00Z', 'bg', { timeZone: 'Europe/Sofia' })
 *   // → "17:30"
 */
export function formatTime(
  value: Date | string | number,
  locale: SupportedLocale,
  options: TimeZoneOptions,
): string {
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(toDate(value));
}

/**
 * Formats both date and time in a locale-appropriate combined string.
 *
 * @example
 *   formatDateTime('2024-06-07T14:30:00Z', 'en', { timeZone: 'UTC' })
 *   // → "Jun 7, 2024, 2:30 PM"
 */
export function formatDateTime(
  value: Date | string | number,
  locale: SupportedLocale,
  options: TimeZoneOptions,
): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(toDate(value));
}

/**
 * Returns a human-readable relative time string (e.g. "3 minutes ago",
 * "in 2 hours") using date-fns for DST-safe unit selection.
 *
 * For past events the result is e.g. "5 minutes ago".
 * For future events the result is e.g. "in 5 minutes".
 *
 * NOTE: This formatter does NOT accept a timeZone because relative time is
 * measured in elapsed wall-clock seconds, not timezone-adjusted calendar
 * units. Timezone only matters for the calendar display (formatDate/Time).
 */
export function formatRelativeTime(
  value: Date | string | number,
  locale: SupportedLocale,
  options?: { addSuffix?: boolean },
): string {
  const date = toDate(value);
  const addSuffix = options?.addSuffix ?? true;
  return formatDistanceToNow(date, {
    locale: dateFnsLocale(locale),
    addSuffix,
  });
}

/**
 * Returns the relative time between two explicit dates.
 * Useful when you want "3 minutes" between a call start and end time,
 * not relative to "now".
 */
export function formatRelativeBetween(
  from: Date | string | number,
  to: Date | string | number,
  locale: SupportedLocale,
  options?: { addSuffix?: boolean },
): string {
  return formatDistance(toDate(from), toDate(to), {
    locale: dateFnsLocale(locale),
    addSuffix: options?.addSuffix ?? false,
  });
}

// ---------------------------------------------------------------------------
// Number and currency formatters
// ---------------------------------------------------------------------------

export interface NumberFormatOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: 'standard' | 'compact' | 'scientific' | 'engineering';
}

/**
 * Formats a number using locale-appropriate thousands separators and decimal
 * marks.
 *
 * @example
 *   formatNumber(1234567.89, 'de', { maximumFractionDigits: 2 })
 *   // → "1.234.567,89"
 */
export function formatNumber(
  value: number,
  locale: SupportedLocale,
  options: NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits,
    notation: options.notation,
  }).format(value);
}

export interface CurrencyFormatOptions extends NumberFormatOptions {
  /**
   * ISO 4217 currency code, e.g. 'EUR', 'USD', 'BGN'.
   * Defaults to 'EUR' — override per hotel's configured currency.
   */
  currency?: string;
  /** Intl currency display style. Defaults to 'symbol'. */
  currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
}

/**
 * Formats a monetary value with locale-appropriate symbol, grouping, and
 * decimal conventions.
 *
 * @example
 *   formatCurrency(1234.5, 'bg', { currency: 'BGN' })
 *   // → "1 234,50 лв."
 *
 *   formatCurrency(1234.5, 'en', { currency: 'USD' })
 *   // → "$1,234.50"
 */
export function formatCurrency(
  value: number,
  locale: SupportedLocale,
  options: CurrencyFormatOptions = {},
): string {
  const {
    currency = 'EUR',
    currencyDisplay = 'symbol',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

// ---------------------------------------------------------------------------
// Duration formatter (calls, operational use)
// ---------------------------------------------------------------------------

/**
 * Formats a duration in seconds as "mm:ss" or "h:mm:ss" for call displays.
 * Not locale-dependent (numeric format is universal for operational use).
 *
 * @example
 *   formatDuration(3725) // → "1:02:05"
 *   formatDuration(125)  // → "2:05"
 */
export function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${mm}:${ss}`;
  }
  return `${minutes}:${ss}`;
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface Formatters {
  formatDate: (value: Date | string | number, tz?: string) => string;
  formatTime: (value: Date | string | number, tz?: string) => string;
  formatDateTime: (value: Date | string | number, tz?: string) => string;
  formatRelativeTime: (value: Date | string | number, addSuffix?: boolean) => string;
  formatRelativeBetween: (
    from: Date | string | number,
    to: Date | string | number,
    addSuffix?: boolean,
  ) => string;
  formatNumber: (value: number, options?: NumberFormatOptions) => string;
  formatCurrency: (value: number, options?: CurrencyFormatOptions) => string;
  formatDuration: (totalSeconds: number) => string;
}

/**
 * Returns a set of locale-aware formatters pre-bound to the active locale and
 * the provided hotel `hotelTimeZone`.
 *
 * Pass the hotel's IANA timezone (from the resolved hotel context) as
 * `hotelTimeZone`. When you need to show the user's local time instead, pass
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 *
 * @example
 *   const f = useFormatters('Europe/Sofia');
 *   f.formatDateTime(call.startedAt);  // hotel timezone
 *   f.formatDate(reservation.checkIn); // still hotel tz by default
 */
export function useFormatters(hotelTimeZone: string): Formatters {
  const locale = useLocale();

  return useMemo<Formatters>(
    () => ({
      formatDate: (value, tz = hotelTimeZone) => formatDate(value, locale, { timeZone: tz }),
      formatTime: (value, tz = hotelTimeZone) => formatTime(value, locale, { timeZone: tz }),
      formatDateTime: (value, tz = hotelTimeZone) =>
        formatDateTime(value, locale, { timeZone: tz }),
      formatRelativeTime: (value, addSuffix = true) =>
        formatRelativeTime(value, locale, { addSuffix }),
      formatRelativeBetween: (from, to, addSuffix = false) =>
        formatRelativeBetween(from, to, locale, { addSuffix }),
      formatNumber: (value, options) => formatNumber(value, locale, options),
      formatCurrency: (value, options) => formatCurrency(value, locale, options),
      formatDuration,
    }),
    [locale, hotelTimeZone],
  );
}

// Re-export date-fns difference helpers for convenient use in components.
export {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
};
